import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, Receipt, Download, Calendar, CreditCard, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import jsPDF from 'jspdf';

const AdminPengeluaran = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isReportMode = mode === 'report';
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [sources, setSources] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    funding_source_id: '',
    expense_date: '',
    category: '',
    amount: '',
    description: '',
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sourcesResp, expensesResp, settingsResp] = await Promise.all([
        api.get('/admin/funding-sources'),
        api.get('/admin/expenses', { params: { start_date: dateRange.start, end_date: dateRange.end } }),
        api.get('/settings/public'),
      ]);
      setSources(Array.isArray(sourcesResp.data) ? sourcesResp.data : []);
      setExpenses(Array.isArray(expensesResp.data) ? expensesResp.data : []);
      try {
        const raw = settingsResp?.data?.expense_categories;
        const parsed = raw ? JSON.parse(raw) : [];
        setExpenseCategories(Array.isArray(parsed) ? parsed.map((v) => String(v || '').trim()).filter(Boolean) : []);
      } catch {
        setExpenseCategories([]);
      }
    } catch {
      toast.error('Gagal memuat data pengeluaran');
    } finally {
      setLoading(false);
    }
  }, [dateRange.end, dateRange.start]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch {
      navigate('/admin/login');
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  const sourcesById = useMemo(() => {
    const m = new Map();
    (Array.isArray(sources) ? sources : []).forEach((s) => m.set(s.id, s));
    return m;
  }, [sources]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const summary = useMemo(() => {
    const out = {
      bank: { count: 0, total: 0 },
      cash: { count: 0, total: 0 },
      total_count: 0,
      total_amount: 0,
    };
    (Array.isArray(expenses) ? expenses : []).forEach((e) => {
      const src = sourcesById.get(e.funding_source_id);
      const type = String(src?.type || '').toLowerCase();
      const amount = Number(e.amount || 0) || 0;
      if (type === 'cash') {
        out.cash.count += 1;
        out.cash.total += amount;
      } else {
        out.bank.count += 1;
        out.bank.total += amount;
      }
      out.total_count += 1;
      out.total_amount += amount;
    });
    return out;
  }, [expenses, sourcesById]);

  const categoryOptions = useMemo(() => {
    const base = Array.isArray(expenseCategories) ? expenseCategories : [];
    const current = String(formData.category || '').trim();
    const merged = current ? [...base, current] : base;
    return Array.from(new Set(merged.filter(Boolean)));
  }, [expenseCategories, formData.category]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('ZERO FUTSAL', pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(14);
    doc.text('Laporan Pengeluaran', pageWidth / 2, 30, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Periode: ${dateRange.start} - ${dateRange.end}`, pageWidth / 2, 38, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Ringkasan', 20, 55);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    let y = 65;
    const lineHeight = 8;

    doc.setFillColor(34, 197, 94);
    doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('Kategori', 25, y);
    doc.text('Jumlah', 110, y);
    doc.text('Total', 150, y);

    doc.setTextColor(0, 0, 0);
    y += lineHeight + 2;

    doc.text('Cash', 25, y);
    doc.text(String(summary.cash.count), 110, y);
    doc.text(formatCurrency(summary.cash.total), 150, y);
    y += lineHeight;

    doc.text('Bank', 25, y);
    doc.text(String(summary.bank.count), 110, y);
    doc.text(formatCurrency(summary.bank.total), 150, y);
    y += lineHeight + 2;

    doc.setFont('helvetica', 'bold');
    doc.setFillColor(229, 231, 235);
    doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');
    doc.text('TOTAL', 25, y);
    doc.text(String(summary.total_count), 110, y);
    doc.text(formatCurrency(summary.total_amount), 150, y);

    y += lineHeight + 15;

    doc.setFontSize(12);
    doc.text('Detail Pengeluaran', 20, y);
    y += 10;

    doc.setFontSize(8);
    doc.setFillColor(34, 197, 94);
    doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('ID', 22, y);
    doc.text('Tanggal', 42, y);
    doc.text('Sumber Dana', 62, y);
    doc.text('Kategori', 112, y);
    doc.text('User', 140, y);
    doc.text('Nominal', 170, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += lineHeight;

    (expenses || []).slice(0, 30).forEach((e, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      const bgColor = index % 2 === 0 ? [249, 250, 251] : [255, 255, 255];
      doc.setFillColor(...bgColor);
      doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');

      const src = sourcesById.get(e.funding_source_id);
      doc.text(String(e.id || '').substring(0, 8), 22, y);
      doc.text(String(e.expense_date || '-'), 42, y);
      doc.text(String(src?.name || '-').substring(0, 18), 62, y);
      doc.text(String(e.category || '-').substring(0, 12), 112, y);
      doc.text(String(e.created_by_user?.name || '-').substring(0, 10), 140, y);
      doc.text(formatCurrency(e.amount), 170, y, { align: 'right' });
      y += lineHeight;
    });

    y += 10;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Digenerate pada: ${new Date().toLocaleString('id-ID')}`, 20, y);
    doc.text('Zero Futsal - Sistem Booking', pageWidth - 20, y, { align: 'right' });

    doc.save(`pengeluaran_zero_futsal_${dateRange.start}_${dateRange.end}.pdf`);
    toast.success('Pengeluaran berhasil di-export ke PDF');
  };

  const resetForm = () => {
    setEditingExpense(null);
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setFormData({
      funding_source_id: sources?.[0]?.id || '',
      expense_date: `${yyyy}-${mm}-${dd}`,
      category: '',
      amount: '',
      description: '',
    });
  };

  const openCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEdit = (exp) => {
    setEditingExpense(exp);
    setFormData({
      funding_source_id: exp.funding_source_id || '',
      expense_date: exp.expense_date || '',
      category: exp.category || '',
      amount: String(exp.amount ?? ''),
      description: exp.description || '',
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(String(formData.amount ?? '').replace(/[^0-9.-]/g, ''));
    if (!formData.funding_source_id) {
      toast.error('Sumber dana wajib dipilih');
      return;
    }
    if (!formData.expense_date) {
      toast.error('Tanggal wajib diisi');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Nominal tidak valid');
      return;
    }
    try {
      const payload = {
        funding_source_id: formData.funding_source_id,
        expense_date: formData.expense_date,
        category: String(formData.category || '').trim() || null,
        amount,
        description: String(formData.description || '').trim() || null,
      };
      if (editingExpense) {
        await api.put(`/admin/expenses/${editingExpense.id}`, payload);
        toast.success('Pengeluaran berhasil diupdate');
      } else {
        await api.post('/admin/expenses', payload);
        toast.success('Pengeluaran berhasil ditambahkan');
      }
      setShowDialog(false);
      resetForm();
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengeluaran');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Yakin ingin menghapus pengeluaran ini?')) return;
    try {
      await api.delete(`/admin/expenses/${expenseId}`);
      toast.success('Pengeluaran berhasil dihapus');
      loadAll();
    } catch {
      toast.error('Gagal menghapus pengeluaran');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">
                {isReportMode ? 'Laporan' : 'Pengeluaran'}
              </span>
            </div>
            <Link
              to="/admin/dashboard"
              className="text-zinc-400 hover:text-primary font-medium transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {isReportMode ? (
            <div className="flex gap-4 mb-8">
              <Link
                to="/admin/reports/pemasukan"
                className={`px-6 py-3 font-bold uppercase tracking-wide transition-colors ${
                  location.pathname.includes('/pemasukan')
                    ? 'bg-primary text-black'
                    : 'bg-zinc-900 text-zinc-300 hover:text-primary border border-zinc-800'
                }`}
              >
                Pemasukan
              </Link>
              <Link
                to="/admin/reports/pengeluaran"
                className={`px-6 py-3 font-bold uppercase tracking-wide transition-colors ${
                  location.pathname.includes('/pengeluaran')
                    ? 'bg-primary text-black'
                    : 'bg-zinc-900 text-zinc-300 hover:text-primary border border-zinc-800'
                }`}
              >
                Pengeluaran
              </Link>
            </div>
          ) : null}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white">
              {isReportMode ? 'Laporan Pengeluaran' : 'Pengeluaran'}
            </h1>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2">
                <Calendar className="w-4 h-4 text-zinc-400" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="bg-transparent text-white text-sm outline-none"
                />
                <span className="text-zinc-500">-</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="bg-transparent text-white text-sm outline-none"
                />
              </div>

              {!isReportMode ? (
                <button
                  onClick={openCreate}
                  className="bg-primary text-black hover:bg-primary-hover rounded-none skew-btn px-6 py-2 font-bold uppercase tracking-wider transition-all duration-200 shadow-[4px_4px_0px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_0px_#FFFFFF] flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Tambah
                </button>
              ) : null}

              <button
                onClick={exportToPDF}
                disabled={loading}
                className="bg-primary text-black hover:bg-primary-hover rounded-none skew-btn px-6 py-2 font-bold uppercase tracking-wider transition-all duration-200 shadow-[4px_4px_0px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_0px_#FFFFFF] disabled:opacity-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-zinc-900 border-l-4 border-green-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Banknote className="w-8 h-8 text-green-500" />
                    <span className="text-2xl font-black text-white">{summary.cash.count}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Cash</div>
                  <div className="text-lg font-bold text-green-400">{formatCurrency(summary.cash.total)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-purple-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <CreditCard className="w-8 h-8 text-purple-500" />
                    <span className="text-2xl font-black text-white">{summary.bank.count}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Bank</div>
                  <div className="text-lg font-bold text-purple-400">{formatCurrency(summary.bank.total)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Receipt className="w-8 h-8 text-primary" />
                    <span className="text-2xl font-black text-white">{summary.total_count}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Total Pengeluaran</div>
                  <div className="text-lg font-bold text-primary">{formatCurrency(summary.total_amount)}</div>
                </div>
              </div>

              <h2 className="text-2xl font-heading font-bold uppercase text-white mb-6">Detail Pengeluaran</h2>
              <div className="overflow-x-auto bg-zinc-900 border border-zinc-800 rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">ID</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Tanggal</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Sumber Dana</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Tipe</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Kategori</th>
                      <th className="text-right py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Nominal</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">User</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Keterangan</th>
                      {!isReportMode ? (
                        <th className="text-right py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Aksi</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.length === 0 ? (
                      <tr>
                        <td colSpan={isReportMode ? 8 : 9} className="text-center py-8 text-zinc-500">
                          Tidak ada pengeluaran dalam periode ini
                        </td>
                      </tr>
                    ) : (
                      expenses.map((exp) => {
                        const src = sourcesById.get(exp.funding_source_id);
                        const type = String(src?.type || '').toLowerCase() === 'cash' ? 'cash' : 'bank';
                        return (
                          <tr key={exp.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                            <td className="py-4 px-4 text-sm text-white font-mono">{String(exp.id || '').substring(0, 8)}</td>
                            <td className="py-4 px-4 text-sm text-white">{exp.expense_date || '-'}</td>
                            <td className="py-4 px-4 text-sm text-white">{src?.name || '-'}</td>
                            <td className="py-4 px-4">
                              <span
                                className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                                  type === 'cash' ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'
                                }`}
                              >
                                {type}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-white">{exp.category || '-'}</td>
                            <td className="py-4 px-4 text-sm text-white text-right font-semibold">{formatCurrency(exp.amount)}</td>
                            <td className="py-4 px-4 text-sm text-zinc-300">
                              {exp.created_by_user?.name ? (
                                <div className="flex flex-col">
                                  <span className="text-white font-semibold">{exp.created_by_user.name}</span>
                                  <span className="text-xs text-zinc-500">{exp.created_by_user.email || '-'}</span>
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="py-4 px-4 text-sm text-zinc-300">{exp.description || '-'}</td>
                            {!isReportMode ? (
                              <td className="py-4 px-4 text-right">
                                <div className="inline-flex gap-2">
                                  <button
                                    onClick={() => openEdit(exp)}
                                    className="bg-transparent border border-primary text-primary hover:bg-primary/10 px-3 py-1 rounded text-xs font-bold uppercase inline-flex items-center gap-2"
                                  >
                                    <Edit className="w-3 h-3" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(exp.id)}
                                    className="bg-red-600 text-white hover:bg-red-700 px-3 py-1 rounded text-xs font-bold uppercase inline-flex items-center gap-2"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    Hapus
                                  </button>
                                </div>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {!isReportMode ? (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold uppercase">
                {editingExpense ? 'Edit Pengeluaran' : 'Tambah Pengeluaran'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Sumber Dana</label>
                <select
                  value={formData.funding_source_id}
                  onChange={(e) => setFormData({ ...formData, funding_source_id: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 transition-all outline-none"
                >
                  <option value="">Pilih sumber dana</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Tanggal</label>
                <input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Kategori</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 transition-all outline-none"
                >
                  <option value="">Pilih kategori</option>
                  {categoryOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Nominal</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 transition-all outline-none"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Keterangan (Opsional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md px-4 py-3 transition-all outline-none min-h-[96px]"
                  placeholder="Contoh: Bayar listrik, beli bola, dll"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full bg-primary text-black hover:bg-primary/90 font-bold uppercase rounded-md h-12 transition-all"
                >
                  Simpan
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
};

export default AdminPengeluaran;
